// src/features/auth/authApi.js
import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryV1 } from '../baseQueryV1';
import { setUser } from '../slice/userSlice';
import { firebaseApi } from '../firebase/firebaseApi';
import { getFirebaseToken } from '../../../config/firebase';

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryV1,
  tagTypes: ['UserDetails'],
  endpoints: (builder) => ({
    logout: builder.mutation({
      query: () => ({
        url: 'auth/logout', //  login endpoint
        method: 'POST',
      }),
    }),
    login: builder.mutation({
      query: (credentials) => ({
        url: 'auth/login', //  login endpoint
        method: 'POST',
        body: credentials,
        credentials: 'include',
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser({ ...data.user }));

          // Save FCM token after successful login (for non-ADMIN users)
          if (data.user) {
            try {
              const token = await getFirebaseToken();
              if (token) {
                await dispatch(
                  firebaseApi.endpoints.saveToken.initiate({
                    role: data.user.role,
                    token,
                    employee_id: data.user.employee_id,
                  }),
                ).unwrap();
              }
            } catch (fcmError) {
              console.error('Error retrieving or saving FCM token during login:', fcmError);
            }
          }
          // Socket auth now uses the HttpOnly cookie set by the server.
        } catch (error) {
          console.error('Fetching user details failed:', error);
        }
      },
      transformResponse: (res) => {
        return res.data;
      },
    }),
    getDetails: builder.query({
      query: () => 'users/details',
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          console.error('Fetching user details failed:', error);
        }
      },
      providesTags: ['UserDetails'],
      transformResponse: (res) => res?.data,
    }),
    addEmployee: builder.mutation({
      query: (payload) => ({
        url: 'users', //  add employee endpoint
        method: 'POST',
        body: payload,
      }),
      transformResponse: (res) => res?.data,
    }),
    activeInactiveUser: builder.mutation({
      query: (id) => ({
        url: `auth/active-inactive-user/${id}`, //  active/inactive user endpoint
        method: 'PUT',
      }),
      transformResponse: (res) => res,
    }),
  }),
});

export const {
  useLoginMutation,
  useAddEmployeeMutation,
  useGetDetailsQuery,
  useLogoutMutation,
  useActiveInactiveUserMutation,
} = authApi;
